import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { NumberSliderValue } from './models/number-slider-value.model';
import { ComponentChanges } from '../../../models/component-changes.model';
import { Marker } from './models/marker.enum';

const NumberSliderComponentProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberSliderComponent),
  multi: true,
};

/**
 * The number slider component
 */
@Component({
  selector: 'ideom-number-slider',
  templateUrl: './number-slider.component.html',
  styleUrls: ['./number-slider.component.scss'],
  providers: [NumberSliderComponentProvider],
})
export class NumberSliderComponent implements AfterViewInit, ControlValueAccessor, OnChanges, OnDestroy {
  /**
   * Contains the minimum value for slider
   * @default 0
   */
  @Input() min = 0;

  /**
   * Contains the maximum value for slider
   * @default 100
   */
  @Input() max = 100;

  /**
   * Contains the step value for slider
   * @default 1
   */
  @Input() step = 1;

  /**
   * Contains a value indicating that this slider has range value
   * @default false
   */
  @Input() range: boolean;

  /**
   * Contains the element ref for container
   * @ignore
   */
  @ViewChild('container') container: ElementRef;

  /**
   * Contains a value to store start value for slider
   */
  start = 0;

  /**
   * Contains a value to store end value for slider
   */
  end = 0;

  /**
   * Contains a value indicating that control is disabled
   */
  isDisabled: boolean;

  /**
   * Contains a handler for control value change invocation
   */
  onChange: (value: NumberSliderValue | number) => void;

  /**
   * Contains a handler for control touched state invocation
   */
  onTouched: () => void;

  /**
   * Contains a value for slider width in pixels
   */
  sliderWidth = 0;

  /**
   * Contains a value for slider left offset in pixels
   */
  sliderOffset = 0;

  /**
   * Contains a value indicating that slider is sliding now
   */
  sliding: boolean;

  /**
   * Contains a value to indicate current marker
   */
  private marker: Marker;

  /**
   * Contains a value for slider dimensions
   */
  private dimensions: DOMRect;

  /**
   * Creates an instance of NumberSliderComponent
   *
   * @param changeDetectorRef The ChangeDetectorRef service
   * @param document The document service
   */
  constructor(private changeDetectorRef: ChangeDetectorRef, @Inject(DOCUMENT) private document: any) {
    this.addDocumentEvents();
  }

  /**
   * @ignore
   */
  ngAfterViewInit(): void {
    this.dimensions = this.getSliderDimensions();
    this.updateView();
  }

  /**
   * @ignore
   */
  ngOnChanges(changes: ComponentChanges<NumberSliderComponent>): void {
    if (changes.min || changes.max || changes.step) {
      this.updateView();
    }
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    this.removeDocumentEvents();
  }

  /**
   * @ignore
   */
  writeValue(obj: NumberSliderValue | number): void {
    if (obj) {
      if (this.range) {
        obj = obj as NumberSliderValue;
        this.start = obj.min;
        this.end = obj.max;
      } else {
        this.end = obj as number;
      }
      this.updateView();
    }
  }

  /**
   * @ignore
   */
  registerOnChange(fn: (value: NumberSliderValue | number) => void): void {
    this.onChange = fn;
  }

  /**
   * @ignore
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * @ignore
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * This method is used for updating values on control mouse down
   * @param event The mouse event
   */
  onMouseDown(event: MouseEvent): void {
    this.dimensions = this.getSliderDimensions();
    if (!this.isDisabled && this.isSliderEvent(event)) {
      if (this.range) {
        const offset = this.getOffset(event);
        const value = this.pixelsToValue(offset);
        if (value <= this.start) {
          this.marker = Marker.Start;
        } else if (value >= this.end) {
          this.marker = Marker.End;
        } else {
          const isClosestToStart = value - this.start < this.end - value;
          if (isClosestToStart) {
            this.marker = Marker.Start;
          } else {
            this.marker = Marker.End;
          }
        }
      } else {
        this.marker = Marker.End;
      }

      this.updateValues(event);
      this.updateView();
      this.sliding = true;
      this.onTouched && this.onTouched();
    }
  }

  /**
   * This method is used for handling mouse up event on control
   * @param event The mouse up event
   */
  onMouseUp(event: MouseEvent): void {
    if (this.sliding) {
      this.updateValues(event);
      this.updateView();
      this.sliding = false;
      this.marker = null;
    }
  }

  /**
   * This method is used for handling moving mouse over the control
   * @param event The mouse move event
   */
  onMouseMove(event: MouseEvent): void {
    if (this.sliding) {
      this.updateValues(event);
      this.updateView();
    }
  }

  /**
   * This method is used for recalculating values according to mouse coordinates
   * @param event The current mouse event
   */
  private updateValues(event: MouseEvent): void {
    const offset = this.getOffset(event);
    const value = this.pixelsToValue(offset);

    // Set step value closest to the value
    let closestValue = Math.round(value / this.step) * this.step + this.min;
    let isValueChanged = false;

    // Check which value we should change: start or end
    if (this.marker) {
      if (this.marker === Marker.Start) {
        if (closestValue > this.end) {
          closestValue = this.end;
        }
        if (this.start !== closestValue) {
          this.start = closestValue;
          isValueChanged = true;
        }
      }
      if (this.marker === Marker.End) {
        if (closestValue < this.start) {
          closestValue = this.start;
        }
        if (this.end !== closestValue) {
          this.end = closestValue;
          isValueChanged = true;
        }
      }
    } else {
      if (this.end !== closestValue) {
        this.end = closestValue;
        isValueChanged = true;
      }
    }

    if (isValueChanged && this.onChange) {
      this.onChange(this.range ? { min: this.start, max: this.end } : this.end);
    }
  }

  /**
   * This method is used for updating control view according to new values
   */
  private updateView(): void {
    if (this.dimensions) {
      let offset = 0;
      if (this.start) {
        offset = this.valueToPixels(this.start);
      }

      let width = 0;
      if (this.end) {
        width = this.valueToPixels(this.end - this.start);
      }

      this.sliderOffset = offset;
      this.sliderWidth = width;
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * This method is used for calculating offset/width pixels from real value
   * @param value The control value
   * @returns Returns the number value in pixels
   */
  private valueToPixels(value: number): number {
    const pixelsPerItem = this.dimensions.width / (this.max - this.min);
    return (value - this.min) * pixelsPerItem;
  }

  /**
   * This method is used for calculating control value from pixels value of mouse event
   * @param pixels The control pixels
   * @returns Returns the number value for control
   */
  private pixelsToValue(pixels: number): number {
    const stepsCount = (this.max - this.min) / this.step;
    const pixelsPerStep = this.dimensions.width / stepsCount;
    const step = Math.round(pixels / pixelsPerStep);
    const itemsPerStep = (this.max - this.min) / stepsCount;
    return step * itemsPerStep;
  }

  /**
   * This method is used for checking pointer coordinates to determine
   * if this event relates to the control
   * @param event The mouse event
   * @returns Returns a value indicating that mouse event relates to the control
   */
  private isSliderEvent(event: MouseEvent): boolean {
    if (this.dimensions) {
      return (
        event.clientX >= this.dimensions.left &&
        event.clientX <= this.dimensions.right &&
        event.clientY >= this.dimensions.top &&
        event.clientY <= this.dimensions.bottom
      );
    }
    return false;
  }

  /**
   * This method is used for getting control dimensions
   * @returns Returns the control dimensions on page
   */
  private getSliderDimensions(): DOMRect {
    return this.container ? this.container.nativeElement.getBoundingClientRect() : null;
  }

  /**
   * This method is used to register document events
   */
  private addDocumentEvents(): void {
    this.document.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.document.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.document.addEventListener('mousemove', this.onMouseMove.bind(this));
  }

  /**
   * This method is used for unregistering document events
   */
  private removeDocumentEvents(): void {
    this.document.removeEventListener('mousedown', this.onMouseDown.bind(this));
    this.document.removeEventListener('mouseup', this.onMouseUp.bind(this));
    this.document.removeEventListener('mousemove', this.onMouseMove.bind(this));
  }

  /**
   * This method is used for getting slider offset according to the event
   * @param event The mouse event
   * @returns Returns the offset in pixels for control according to event
   */
  private getOffset(event: MouseEvent): number {
    const dimensions = this.getSliderDimensions();
    const left = dimensions.left;
    const right = dimensions.right;

    let offsetX: number;
    if (event.clientX < left) {
      offsetX = left;
    } else if (event.clientX > right) {
      offsetX = right;
    } else {
      offsetX = event.clientX;
    }

    return offsetX - left;
  }
}
